/* eslint-disable global-require */
import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { render } from 'react-dom';
import './index.css';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { initOptionsAndValuesTranslations } from '@michelin/select-options-provider';
import { DialogProvider, LoadingBounce } from '@michelin/acid-components';
import {
  APPS,
  CentralProvider,
  CentralProviderContext,
  initReactI18next,
  Languages,
  THEMES,
  usePermissions,
  useTranslation,
} from '@michelin/central-provider';
import { createBrowserHistory } from 'history';
import { loadMessages as devExLoadMessages, locale as devExSetLocale } from 'devextreme/localization';
import frMessages from 'devextreme/localization/messages/fr.json';
import { initMaestroTranslations, prefixedTranslations, sharedTranslations } from '@michelin/maestro-common-translations';
import i18n from '@michelin/central-provider/dist/translations/CentralTranslation';
import { SnackbarProvider } from 'notistack';
import { runningOnCanvas } from '@michelin/central-provider/dist/utils/canvas';
import Routes from './Routes';
import GLOBAL_CONFIG from './global-variables';

initMaestroTranslations(i18n, initReactI18next, {
  en_US: {
    ...require('./i18n/CommercialERS/en_US.json'),
    ...require('./i18n/CommercialLocation/en_US.json'),
    ...require('./i18n/CommercialMechanicalServices/en_US.json'),
    ...require('./i18n/CommercialNetworks/en_US.json'),
    ...require('./i18n/CommercialOverrides/en_US.json'),
    ...require('./i18n/CommercialProducts/en_US.json'),
    ...require('./i18n/CommercialTireServices/en_US.json'),
    ...require('./i18n/Contacts/mechanicCertifications_en_US.json'),
    ...require('./i18n/Contacts/mechanicCertifications_en_US.json'),
    ...require('./i18n/Contacts/selectContact_en_US.json'),
    ...require('./i18n/FileUpload/en_US.json'),
    ...require('./i18n/RetailServices/en_US.json'),
    ...require('./i18n/MassSTUpdate/en_US.json'),
    ...require('./i18n/en_US.json'),
  },
  fr_CA: {
    ...require('./i18n/CommercialERS/fr_CA.json'),
    ...require('./i18n/CommercialLocation/fr_CA.json'),
    ...require('./i18n/CommercialMechanicalServices/fr_CA.json'),
    ...require('./i18n/CommercialNetworks/fr_CA.json'),
    ...require('./i18n/CommercialOverrides/fr_CA.json'),
    ...require('./i18n/CommercialProducts/fr_CA.json'),
    ...require('./i18n/CommercialTireServices/fr_CA.json'),
    ...require('./i18n/Contacts/mechanicCertifications_fr_CA.json'),
    ...require('./i18n/Contacts/mechanicCertifications_fr_CA.json'),
    ...require('./i18n/Contacts/selectContact_fr_CA.json'),
    ...require('./i18n/FileUpload/fr_CA.json'),
    ...require('./i18n/RetailServices/fr_CA.json'),
    ...require('./i18n/MassSTUpdate/fr_CA.json'),
    ...require('./i18n/fr_CA.json'),
  },
}, [
  sharedTranslations.dates,
  prefixedTranslations(sharedTranslations.country_codes, 'COUNTRY_CODE|'),
]);

initOptionsAndValuesTranslations(i18n);

const history = createBrowserHistory();

devExLoadMessages(frMessages);

interface RednerLoggedAppProps {
  setLanguages: any,
  languages: Languages[]
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const RenderLoggedApp = (props: RednerLoggedAppProps): JSX.Element => {
  // eslint-disable-next-line no-shadow
  const { i18n } = useTranslation();
  const permissions = usePermissions();
  const availLanguages = [Languages.EN_US, Languages.FR_CA];
  const {
    setCurrentTheme, currentTheme, setCurrentLanguage, currentLanguage,
  } = React.useContext(CentralProviderContext);

  useEffect(() => {
    if (runningOnCanvas() && currentLanguage !== permissions.profile?.profile.language) setCurrentLanguage(permissions.profile?.profile.language as Languages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, currentLanguage]);
  useEffect(() => setCurrentTheme('michelinLightBC' as any), [setCurrentTheme, currentTheme]);

  if (props.languages.sort().join('.') !== availLanguages.sort().join('.')) {
    props.setLanguages(availLanguages);
    return <LoadingBounce style={{ height: '100vh' }} />;
  }

  devExSetLocale(i18n.language.split('_')[0]);

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <DialogProvider>
        <SnackbarProvider anchorOrigin={{ vertical: 'top', horizontal: 'center' }} maxSnack={5}>
          <Routes key={`lang-${i18n.language}`} />
        </SnackbarProvider>
      </DialogProvider>
    </MuiPickersUtilsProvider>
  );
};

const renderApp = () => {
  const LaunchApp = () => {
    const [languages, setLanguages] = useState([Languages.EN_US, Languages.FR_CA]);
    return (
      <CentralProvider
        redirect="/"
        application={APPS.DEALER_PROFILE}
        redirectToPreviousRoute
        onLogin={(previous: string) => history.push(previous)}
        useCentralProviderDebugger={false}
        useTranslationDebugger={false}
        allowGrantLocationSelector={false}
        languages={languages}
        themes={{
          on: THEMES.michelinLightBC,
        }}
        disableTranslationsManager
        useMdmId={GLOBAL_CONFIG.useMdmIds}
        devexLightThemeCSS={`${process.env.PUBLIC_URL}/css/dx.material.brand-center.css`}
        devexDarkThemeCSS={`${process.env.PUBLIC_URL}/css/dx.material.brand-center.css`}
      >
        <QueryClientProvider client={queryClient}>
          <RenderLoggedApp setLanguages={setLanguages} languages={languages} />
        </QueryClientProvider>
      </CentralProvider>
    );
  };
  render(
    <LaunchApp />,
    document.getElementById('root'),
  );
};

renderApp();
